import { useContext, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';

import { NativeDataContext } from 'bundles/ent-website/components/NativeDataContext';
import type {
  FindByEnterprisePromotionIdQuery as FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByEnterprisePromotionIdQuery';
import type {
  FindByPromoCodeQuery as FindByPromoCodeQueryType,
  FindByPromoCodeQueryVariables,
} from 'bundles/ent-website/utils/__generated__/FindByPromoCodeQuery';
import {
  findByEnterprisePromotionIdQuery,
  findByPromoCodeQuery,
} from 'bundles/ent-website/utils/withEnterprisePromotionQuery';

function PromoDataSource() {
  const { query } = useLocation();
  const promoCode = query?.promoCode;

  const { data: findByPromoCodeData } = useQuery<FindByPromoCodeQueryType, FindByPromoCodeQueryVariables>(
    findByPromoCodeQuery,
    {
      skip: !promoCode,
      variables: { promoCodeId: promoCode },
      errorPolicy: 'all',
    }
  );

  const enterprisePromotionV1 = findByPromoCodeData?.EnterprisePromotionsV1Resource?.findByPromoCode?.elements?.[0];
  const enterprisePromotion = useMemo(
    () =>
      !enterprisePromotionV1 ? {} : { id: enterprisePromotionV1.id, promotionId: enterprisePromotionV1.promotionId },
    [enterprisePromotionV1]
  );

  const { data: findByPromoIdData } = useQuery<
    FindByEnterprisePromotionIdQueryType,
    FindByEnterprisePromotionIdQueryVariables
  >(findByEnterprisePromotionIdQuery, {
    skip: !enterprisePromotion?.id,
    variables: { id: enterprisePromotion.id ?? '' },
    errorPolicy: 'all',
  });

  const promotion = findByPromoIdData?.PromotionsV3Resource?.byEnterprisePromotionId?.elements?.[0];

  const { setNativeData } = useContext(NativeDataContext);

  useEffect(() => {
    setNativeData('promo', {
      promoCode,
      percent: promotion?.discountPercent,
      name: promotion?.promotionName,
      promotion,
      enterprisePromotion,
    });
  }, [setNativeData, promoCode, promotion, enterprisePromotion]);

  return null;
}

export default PromoDataSource;
